import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  AVATAR_MAX_ZOOM,
  avatarCropRect,
  clampAvatarView,
  clampLogoView,
  de,
  initialAvatarView,
  initialLogoView,
  LOGO_MAX_ZOOM,
  logoCropRect,
  panAvatarView,
  panLogoView,
  type AvatarView,
} from '@fsv/core';
import { tx } from '@fsv/data';
import type { AvatarCrop } from '../lib/avatarImage';
import { Sheet } from './Sheet';
import './AvatarCropper.css';

type Size = { width: number; height: number };

/**
 * The two ways a square is chosen. A profile picture fills the square (zoom
 * 1 = the short side, no empty corner); a club logo starts with the whole
 * picture inside it (zoom 1 = the long side, transparent margins) and zooming
 * cuts (core/logo.ts). Same {zoom, x, y} view, different arithmetic.
 */
const SHAPES = {
  avatar: {
    maxZoom: AVATAR_MAX_ZOOM,
    side: (image: Size, zoom: number) => Math.min(image.width, image.height) / zoom,
    initial: initialAvatarView,
    clamp: clampAvatarView,
    pan: panAvatarView,
    title: de.avatar.cropTitle,
    hint: de.avatar.cropHint,
  },
  logo: {
    maxZoom: LOGO_MAX_ZOOM,
    side: (image: Size, zoom: number) => Math.max(image.width, image.height) / zoom,
    initial: initialLogoView,
    clamp: clampLogoView,
    pan: panLogoView,
    title: de.logo.cropTitle,
    hint: de.logo.cropHint,
  },
} as const;

/**
 * Choose the square of a profile picture before it is scaled down: drag (or
 * arrow keys) moves the section, two fingers, the wheel or the slider zoom.
 * The arithmetic lives in @fsv/core (avatar.ts); this only translates
 * pointers into it. The <img> shows the picture upright (CSS image-orientation
 * defaults to from-image), the same way createImageBitmap reads it in
 * prepareAvatar — so the crop rectangle means the same pixels in both.
 */
export function AvatarCropper(props: { file: File; onApply: (crop: AvatarCrop) => void; onCancel: () => void }) {
  return <SquareCropper {...props} shape="avatar" onApply={(image, view) => props.onApply(avatarCropRect(image, view))} />;
}

/** The logo's section as fractions of the picture's longer edge (`logoCropRect`). */
export type LogoCrop = ReturnType<typeof logoCropRect>;

export function LogoCropper(props: { file: File; onApply: (crop: LogoCrop) => void; onCancel: () => void }) {
  return <SquareCropper {...props} shape="logo" onApply={(image, view) => props.onApply(logoCropRect(image, view))} />;
}

function SquareCropper({
  file,
  shape,
  onApply,
  onCancel,
}: {
  file: File;
  shape: keyof typeof SHAPES;
  onApply: (image: Size, view: AvatarView) => void;
  onCancel: () => void;
}) {
  const geometry = SHAPES[shape];
  const [url, setUrl] = useState<string | null>(null);
  const [image, setImage] = useState<Size | null>(null);
  const [view, setView] = useState<AvatarView | null>(null);
  const [viewport, setViewport] = useState(280);
  const frame = useRef<HTMLDivElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    const measure = () => setViewport(el.clientWidth || 280);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const zoomTo = (zoom: number) => image && setView((v) => v && geometry.clamp(image, { ...v, zoom }));
  const pan = (dx: number, dy: number) => image && setView((v) => v && geometry.pan(image, v, dx, dy, viewport));

  const onPointerMove = (e: React.PointerEvent) => {
    const last = pointers.current.get(e.pointerId);
    if (!last || !image) return;
    const other = [...pointers.current.entries()].find(([id]) => id !== e.pointerId)?.[1];
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (!other) {
      pan(e.clientX - last.x, e.clientY - last.y);
    } else {
      const before = Math.hypot(last.x - other.x, last.y - other.y);
      const after = Math.hypot(e.clientX - other.x, e.clientY - other.y);
      if (before > 0) setView((v) => v && geometry.clamp(image, { ...v, zoom: (v.zoom * after) / before }));
    }
  };
  const release = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
  };

  // React's onWheel is passive; preventDefault needs a native listener.
  useEffect(() => {
    const el = frame.current;
    if (!el || !image) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setView((v) => v && geometry.clamp(image, { ...v, zoom: v.zoom * Math.exp(-e.deltaY / 400) }));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [image, geometry]);

  let imgStyle: CSSProperties = { visibility: 'hidden' };
  if (image && view) {
    const side = geometry.side(image, view.zoom);
    const scale = viewport / side;
    imgStyle = {
      width: image.width * scale,
      height: image.height * scale,
      left: -(view.x - side / 2) * scale,
      top: -(view.y - side / 2) * scale,
    };
  }

  return (
    <Sheet
      title={geometry.title}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            {tx.app.cancel}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!image || !view}
            data-testid="avatar-crop-apply"
            onClick={() => image && view && onApply(image, view)}
          >
            {de.avatar.apply}
          </button>
        </>
      }
    >
      <div className="cropper">
        <p className="muted cropper__hint">{geometry.hint}</p>
        <div
          ref={frame}
          className={`cropper__frame cropper__frame--${shape}`}
          tabIndex={0}
          role="img"
          aria-label={geometry.hint}
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
          }}
          onPointerMove={onPointerMove}
          onPointerUp={release}
          onPointerCancel={release}
          onKeyDown={(e) => {
            const step = 12;
            const moves: Record<string, [number, number]> = {
              ArrowLeft: [step, 0],
              ArrowRight: [-step, 0],
              ArrowUp: [0, step],
              ArrowDown: [0, -step],
            };
            const move = moves[e.key];
            if (!move) return;
            e.preventDefault();
            pan(...move);
          }}
        >
          {url && (
            <img
              src={url}
              alt=""
              draggable={false}
              style={imgStyle}
              onLoad={(e) => {
                // An SVG without its own size reports 0: measure it as a square, as prepareLogo draws it.
                const size = { width: e.currentTarget.naturalWidth || 256, height: e.currentTarget.naturalHeight || 256 };
                setImage(size);
                setView(geometry.initial(size));
              }}
            />
          )}
        </div>
        <label className="cropper__zoom">
          <span>{de.avatar.zoom}</span>
          <input
            className="range"
            type="range"
            min={1}
            max={geometry.maxZoom}
            step={0.01}
            value={view?.zoom ?? 1}
            disabled={!view}
            onChange={(e) => zoomTo(Number(e.target.value))}
            aria-valuetext={`${Math.round((view?.zoom ?? 1) * 100)} %`}
          />
        </label>
      </div>
    </Sheet>
  );
}
